import { Component, DestroyRef, OnDestroy, OnInit, computed, effect, inject, input, signal } from '@angular/core';
import { Router } from '@angular/router';
import { CreateManageUserAsync } from 'src/app/store/createManageUser/async';
import {
  errorsFetchingClients,
  fetchingClients,
  getClientsOfAdmin,
} from 'src/app/store/createManageUser/selector';
import { ICreateManageUserState, IManageuser } from 'src/app/store/createManageUser/state';
import { Lux } from '@roosevelt/common-ui-lib/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { IClients } from 'src/app/shared/types/shared-state';

@Component({
    selector: 'bmt-view-auth',
    templateUrl: './view-auth.component.html',
    styleUrls: ['./view-auth.component.scss'],
    standalone: false
})
export class ViewAuthComponent implements OnInit, OnDestroy {
  luxId = 'BMT-view-auth' + this.constructor.name;
  private readonly destroyRef = inject(DestroyRef);
  readonly userInfo = input.required<IManageuser>();
  readonly errorsFetchingClients = toSignal(this.lux.get(errorsFetchingClients, this.luxId), { initialValue: '' });
  readonly fetchingClients = toSignal(this.lux.get(fetchingClients, this.luxId), { initialValue: false });
  readonly clientsInfo = toSignal(this.lux.get(getClientsOfAdmin, this.luxId), {
    initialValue: { clients: [], pagination: { totalRecords: 0, limit: 0, offset: 0 }, sort: [] } as IClients
  });
  readonly userContext = computed(() => this.userInfo().contextOfAdmin.split('/'));
  readonly multipleClients = computed(() => !['BMT-GROUP-ADMIN', 'BMT-SUBGROUP-ADMIN', 'BMT-GROUP-DELEGATE', 'BMT-SUBGROUP-DELEGATE'].includes(this.userInfo().adminRole));
  readonly selectedClient = signal('');
  readonly editAuthorizations = signal(false);
  readonly totalRecords = computed(() => this.clientsInfo()?.pagination?.totalRecords || 0);
  readonly adminClientsInfo = computed(() => this.clientsInfo()?.clients || []);

  defaultPageSize = 50;
  constructor(private readonly router: Router, private readonly lux: Lux<{ createManageUserState: ICreateManageUserState }>, private readonly createManageUserAsync: CreateManageUserAsync) {
    effect(() => {
      if (this.fetchingClients() || this.errorsFetchingClients()) {
        return;
      }

      if (!this.adminClientsInfo().length) {
        return;
      }

      const element = document.querySelector('#scrollId');
      element?.scrollIntoView();
    });
  }

  ngOnDestroy(): void {
    this.lux.destroy(this.luxId);
  }

  ngOnInit(): void {
    const userInfo = this.userInfo();
    const adminRole = userInfo.adminRole;
    const singleClientOrSubGroupAdmins = ['BMT-GROUP-ADMIN', 'BMT-SUBGROUP-ADMIN', 'BMT-GROUP-DELEGATE', 'BMT-SUBGROUP-DELEGATE'];
    if (!singleClientOrSubGroupAdmins.includes(adminRole)) {
      this.getClients(0);
    }
  }

  navigateToUserAdmin() {
    this.router.navigate(['userAdmin/manage']);
  }

  toggleTheClient(client: string): void {
    this.selectedClient.set(this.selectedClient() !== client ? client : '');
  }

  getClients(offset: number): void {
    const req = {
      planAcronym: this.userInfo().planAcronyms,
      offset: offset,
      limit: this.defaultPageSize
    };
    this.createManageUserAsync.getClients(req)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  getMoreClients(page: { pageIndex: number }): void {
    this.getClients(page.pageIndex >= 1 ? (page.pageIndex * this.defaultPageSize) : 0);
  }
}
